import { PosthogEventType } from "@repo/app-types/client/client";
import { InterviewDecision } from "@repo/app-types/entities/interview";
import type { ReactNode } from "react";
import { captureAnalyticsEvent } from "../../analytics/analyticsClient.js";

export interface InterviewDecisionWithScore {
  /** The interview score. */
  originalScore: number;
  /** The decision that the user made about the interview. */
  decision: InterviewDecision;
}

/**
 * Checks if a decision appears inconsistent with the assessment score.
 * Low scores (1-2) shouldn't normally be advanced.
 * High scores (4-5) shouldn't normally be rejected.
 */
export function checkIsInconsistentDecision({
  originalScore,
  decision,
}: InterviewDecisionWithScore): boolean {
  // Advanced a low-scoring candidate
  if (decision === InterviewDecision.Advanced && originalScore <= 2) {
    return true;
  }

  // Rejected a high-scoring candidate
  if (decision === InterviewDecision.Rejected && originalScore >= 4) {
    return true;
  }

  return false;
}

/** Renders a description message that explains why feedback is being requested. */
export function renderInconsistentDecisionMessage({
  originalScore,
  decision,
}: InterviewDecisionWithScore): ReactNode {
  if (decision === InterviewDecision.Advanced && originalScore <= 2) {
    return "It looks like you advanced a candidate with a low score. You can share feedback to improve your AI grading.";
  }

  if (decision === InterviewDecision.Rejected && originalScore >= 4) {
    return "It looks like you rejected a candidate with a high score. You can share feedback to improve your AI grading.";
  }

  return null;
}

const sentiments = ["positive", "negative"] as const;

export type AssessmentFeedbackSentiment = (typeof sentiments)[number];

export function checkIsSentiment(
  value: unknown,
): value is AssessmentFeedbackSentiment {
  return sentiments.some((sentiment) => sentiment === value);
}

interface TrackAssessmentFeedbackValues {
  projectId: string;
  interviewId: string;
  /** The score that the user is giving feedback on. */
  originalScore?: number;
  /** The score that the user is suggesting. */
  suggestedScore?: number;
  /** The comments provided by the user. */
  comments?: string;
  /** Whether the user originally clicked the thumbs up or thumbs down. */
  sentiment?: AssessmentFeedbackSentiment;
  /** The decision that the user made when giving this feedback. */
  decision?: InterviewDecision;
}

/** Track assessment score feedback in PostHog. */
export function trackAssessmentFeedback(
  values: TrackAssessmentFeedbackValues,
): void {
  console.log("Tracking assessment feedback", values);
  captureAnalyticsEvent(PosthogEventType.InterviewAssessmentFeedbackSubmit, {
    project_id: values.projectId,
    interview_id: values.interviewId,
    original_score: values.originalScore,
    suggested_score: values.suggestedScore,
    comments: values.comments,
    sentiment: values.sentiment,
    decision: values.decision,
  });
}
